import { DealService } from './DealService.js';

const RECURRING_MODELS = new Set(['monthly', 'weekly', 'quarterly', 'annual']);

export class RevenueSetupService {
    static _isWonDeal(deal) {
        const stage = String(deal?.dealStage || deal?.deal_stage || '');
        return deal?.status === 'won' || stage === 'closed_won' || stage === 'closed';
    }

    static _rowAmount(row) {
        return Number(row?.plannedAmount ?? row?.amount ?? 0) || 0;
    }

    static _rowHasDate(row) {
        return Boolean(row?.invoiceDate || row?.date || row?.invoice_date || row?.dueDate || row?.due_date);
    }

    /**
     * Ignore leftover empty placeholder rows (e.g. the unused custom-schedule
     * template that stays in memory while a quarterly schedule is filled in).
     */
    static _meaningfulRows(rows) {
        if (!Array.isArray(rows)) return [];
        return rows.filter(row => this._rowHasDate(row) || this._rowAmount(row) > 0);
    }

    static _hasFilledSchedule(rows) {
        const meaningful = RevenueSetupService._meaningfulRows(rows);
        return meaningful.length > 0 && meaningful.every(row => RevenueSetupService._rowAmount(row) > 0);
    }

    /**
     * Closed-won revenue setup is complete when the user has configured a
     * payment schedule. Recurring and custom rows are alternative sources —
     * either is enough. Issued invoice schedules or persisted billing details
     * also count (kanban/list payloads often omit nested schedule rows).
     */
    static _hasConfiguredSchedule(deal, schedules = []) {
        const model = deal?.paymentModel || deal?.payment_model || '';

        if (model === 'ongoing') {
            return (Number(deal.retainerAmount) || Number(deal.estimatedAnnualValue) || 0) > 0;
        }

        if (model === 'one_time') {
            const oneTimeOk = (Number(deal.oneTimeAmount) || 0) > 0;
            return oneTimeOk
                || RevenueSetupService._hasFilledSchedule(deal.customScheduleRows)
                || RevenueSetupService._hasFilledSchedule(schedules);
        }

        if (model === 'milestone') {
            return RevenueSetupService._hasFilledSchedule(deal.dealMilestones)
                || RevenueSetupService._hasFilledSchedule(schedules);
        }

        if (
            RevenueSetupService._hasFilledSchedule(deal.recurringScheduleRows)
            || RevenueSetupService._hasFilledSchedule(deal.customScheduleRows)
            || RevenueSetupService._hasFilledSchedule(schedules)
        ) {
            return true;
        }

        // List/kanban: billing fields on the deal prove the schedule was saved.
        const paymentCount = Number(deal.paymentCount || deal.totalInvoices || deal.total_invoices || 0);
        const invoiceAmount = Number(deal.invoiceAmount || deal.invoice_amount || 0);
        const contractValue = Number(deal.contractValue || deal.contract_value || deal.dealAmount || 0);
        const hasInvoiceDate = Boolean(
            deal.expectedFirstInvoiceDate
            || deal.expected_first_invoice_date
            || deal.revenueStartDate
            || deal.revenue_start_date
        );
        if (paymentCount > 1 && contractValue > 0) return true;
        if (paymentCount >= 1 && (invoiceAmount > 0 || hasInvoiceDate)) return true;
        if (RECURRING_MODELS.has(model) && paymentCount >= 1 && contractValue > 0 && hasInvoiceDate) {
            return true;
        }
        return false;
    }

    static computeStatus(deal, schedules = []) {
        const isWon = RevenueSetupService._isWonDeal(deal);

        const hasContractValue = (deal.contractValue || deal.contract_value || deal.dealAmount || 0) > 0;
        const hasPaymentModel = Boolean(deal.paymentModel);
        const hasPaymentTerms = Boolean(deal.paymentTerms);

        if (!hasContractValue || !hasPaymentModel || !hasPaymentTerms) {
            return { status: 'incomplete', label: 'Revenue setup needed' };
        }

        if (isWon && !RevenueSetupService._hasConfiguredSchedule(deal, schedules)) {
            return { status: 'incomplete', label: 'Revenue setup needed' };
        }

        return { status: 'complete', label: 'Complete' };
    }

    static computeCompletionPct(deal, schedules = []) {
        const isWon = RevenueSetupService._isWonDeal(deal);
        const checks = [];

        checks.push((deal.contractValue || deal.contract_value || deal.dealAmount || 0) > 0);
        checks.push(Boolean(deal.paymentModel));
        checks.push(Boolean(deal.paymentTerms));

        if (isWon) {
            checks.push(RevenueSetupService._hasConfiguredSchedule(deal, schedules));
        }

        const passed = checks.filter(Boolean).length;
        const total = checks.length;
        return total > 0 ? Math.round((passed / total) * 100) : 0;
    }

    static async getIncompleteWonDeals() {
        const deals = await DealService.getListView();
        const incomplete = [];
        for (const deal of (deals || []).filter((d) => d.status === 'won')) {
            const setup = RevenueSetupService.computeStatus(deal, []);
            if (setup.status === 'incomplete') {
                incomplete.push({ ...deal, revenueSetupStatus: setup.status });
            }
        }
        return incomplete;
    }
}
